/**
 * Money — formatting and multi-currency display.
 *
 * Prices are authored in `BASE_CURRENCY` cents (`priceCents`). When the player
 * picks a different display currency, amounts are converted at the server-
 * supplied rate. The SAME currency the player sees is what ConvertIQ charges —
 * there is no separate "charge currency". If no rate is known for the chosen
 * currency we fall back to `BASE_CURRENCY` so a price is always visible.
 */
import { ref } from 'vue';
import { localeTag, DEFAULT_CURRENCY, isCurrencyCode, type CurrencyCode } from './locales';
import { activeLocale } from './index';

/** The currency prices are authored in (the server's canonical unit). */
export const BASE_CURRENCY: CurrencyCode = 'EUR';

/** @deprecated Use `BASE_CURRENCY`. Kept for any residual imports. */
export const CHARGE_CURRENCY: CurrencyCode = BASE_CURRENCY;

const STORAGE_KEY = 'fw.currency';

/**
 * Units of `code` per one unit of BASE_CURRENCY, supplied by the server so a
 * stale rate can be corrected without a deploy. Only the identity rate is
 * assumed — every other rate has to be told to us.
 *
 * Both this and the display pick are `ref`s so that every price in the Vue tree
 * repaints the moment either changes. A plain module variable would leave the
 * old figures on screen until something unrelated triggered a render — the bug
 * where switching currency appears to do nothing. `ref` is just a reactive box,
 * so the vanilla callers below are unaffected.
 */
const ratesRef = ref<Partial<Record<CurrencyCode, number>>>({ [BASE_CURRENCY]: 1 });

const displayRef = ref<CurrencyCode>(readStored() ?? DEFAULT_CURRENCY);

function readStored(): CurrencyCode | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return isCurrencyCode(v) ? v : null;
  } catch {
    return null;
  }
}

/** Feed in the server's rate table. Bad or missing entries are dropped, not guessed. */
export function setRates(next: Record<string, unknown> | null | undefined): void {
  const clean: Partial<Record<CurrencyCode, number>> = { [BASE_CURRENCY]: 1 };
  for (const [code, raw] of Object.entries(next ?? {})) {
    const n = typeof raw === 'string' ? Number(raw) : raw;
    if (isCurrencyCode(code) && typeof n === 'number' && Number.isFinite(n) && n > 0) {
      clean[code] = n;
    }
  }
  ratesRef.value = clean;
}

export function displayCurrency(): CurrencyCode {
  return displayRef.value;
}

export function setDisplayCurrency(code: CurrencyCode): void {
  displayRef.value = code;
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch {
    // Storage unavailable — the pick just won't outlive the tab.
  }
}

/** Adopt the account's stored pick. Ignores anything unsupported. */
export function applyAccountCurrency(code: string | null | undefined): void {
  if (isCurrencyCode(code)) setDisplayCurrency(code);
}

/** The currency figures will actually render in, after the rate check. */
export function effectiveCurrency(): CurrencyCode {
  return ratesRef.value[displayRef.value] != null ? displayRef.value : BASE_CURRENCY;
}

/** True when a rate is known for `code`, i.e. it can actually be displayed. */
export function hasRate(code: CurrencyCode): boolean {
  return ratesRef.value[code] != null;
}

/** True when the effective display currency differs from the base (authoring) currency. */
export function isConverted(): boolean {
  return effectiveCurrency() !== BASE_CURRENCY;
}

/**
 * Format base-currency cents for display.
 *
 * Renders in the player's chosen display currency (or an explicit override via
 * `opts.currency`). The same currency the player sees is what ConvertIQ charges
 * — there is no separate "charge" vs "display" split.
 *
 * `exact: true` turns the conversion off: the cents are taken as already being
 * in `opts.currency` (base currency when none is given) and are printed at face
 * value. That is the mode for a record of something that already happened — a
 * past purchase, an issued invoice. Those were charged once, in one currency,
 * and re-pricing them at today's rate every time the player switches their
 * display currency would show a number that matches neither the card statement
 * nor the PDF. Price tags convert; receipts do not.
 */
export function formatMoney(
  cents: number,
  opts: { currency?: CurrencyCode; locale?: string; exact?: boolean } = {},
): string {
  const target = opts.currency ?? (opts.exact ? BASE_CURRENCY : effectiveCurrency());
  const rate = opts.exact ? 1 : (ratesRef.value[target] ?? 1);
  const amount = (cents / 100) * rate;
  const tag = localeTag(opts.locale ?? activeLocale());

  try {
    return new Intl.NumberFormat(tag, {
      style: 'currency',
      currency: target,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    // Exotic tag or an ICU-less build — never let a price crash a screen.
    return `${target} ${amount.toFixed(2)}`;
  }
}

/** Plain number formatting in the active locale (rune counts, damage, ranks). */
export function formatNumber(value: number, opts: Intl.NumberFormatOptions = {}): string {
  try {
    return new Intl.NumberFormat(localeTag(activeLocale()), opts).format(value);
  } catch {
    return String(value);
  }
}

/** Date formatting in the active locale — replaces the ad-hoc `toLocaleDateString` calls. */
export function formatDate(
  value: string | number | Date,
  opts: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  try {
    return new Intl.DateTimeFormat(localeTag(activeLocale()), opts).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}
